<template>
  <div class="signWrap">
    <div class="backBg"></div>
    <div v-if="!isLoading" class="container flex col f-center gap-32">
      <div class="signTop">
        <NuxtLink :to="{name: 'index'}" class="logo">
          <img src="~/assets/images/logo_blue.svg" alt="codepresso logo" />
        </NuxtLink>
      </div>
      <LazySignPasswordChangeEnterEmail
          v-if="passwordChangeAuthEmailFlag === SignPasswordChangeType.ENTER"
          :is-not-having-user="isNotHavingUser"
          @send="sendPasswordChangeAuthEmail"
      />
      <LazySignPasswordChange
          v-else-if="passwordChangeAuthEmailFlag === SignPasswordChangeType.CHANGED"
          :user-email="userEmail"
          @on-change-password="userPasswordChange"
      />
      <LazySignPasswordChangeEmailForm
          v-else
          :password-form-type="passwordChangeAuthEmailFlag"
          :user-email="userEmail"
          @resend="onClickResendEmail"
          @complete="passwordChangeComplete"
      />
      <LazySignCommonFooter />
    </div>
  </div>
</template>

<script setup lang="ts">
import {onMounted, ref} from 'vue';
import {useI18n} from 'vue-i18n';
import {useRoute} from 'vue-router';
import {useUserService} from '~/composables/service/useUserService';
import {LazySignCommonFooter} from '#components';
import {useToastStore} from '~/store/toast/toastStore';
import {ToastType} from '~/components/Common/Toast/model/ToastType';
import {SignPasswordChangeType} from '~/components/Sign/passwordChange/SignPasswordChangeType';
import {getCookie, setCookie} from '~~/package/utils/CookieUtil';
import {CookieKeyType} from '~~/package/mapper/common/model/CookieKeyType';
import {SignType, SignUIType} from '~/components/Sign/enum/SignType';
import sha3 from 'js-sha3';
import {UserPasswordChangeModel} from '~~/package/mapper/user/model/UserPasswordChangeModel';
import {UserType} from '~~/package/mapper/user/model/UserType';

const {t} = useI18n();
const { sha3_256 } = sha3;

const route = useRoute();
const router = useRouter();
const toastStore = useToastStore();
const userService = useUserService();

const passwordChangeAuthEmailFlag = ref(SignPasswordChangeType.NONE);
const userEmail = ref('');
const isLoading = ref(true);
const isNotHavingUser = ref(false);

const RESEND_COOKIE_LIFETIME_SECONDS = 30;

const canResendAuthEmail = (): boolean => {
  return getCookie(CookieKeyType.RESEND_AUTH_MAIL) === undefined;
};

const setResendAuthEmailCookie = () => {
  const now = Date.now();
  const expires = new Date(now + RESEND_COOKIE_LIFETIME_SECONDS * 1000);

  setCookie(CookieKeyType.RESEND_AUTH_MAIL, true, {
    expires,
    maxAge: RESEND_COOKIE_LIFETIME_SECONDS
  });
};

const sendPasswordChangeAuthEmail = async (email: string, isResend = false) => {
  try {
    const isSuccess = await userService.userSendPasswordChangeAuthEmail(email);
    isNotHavingUser.value = false;

    if (isSuccess) {
      toastStore.openToast({
        toastType: ToastType.SUCCESS,
        contents: isResend ? t('password-change-resend-success') : t('password-change-send-success-dot')
      });

      userEmail.value = email;
      passwordChangeAuthEmailFlag.value =
          passwordChangeAuthEmailFlag.value === SignPasswordChangeType.EXPIRED ?
              SignPasswordChangeType.EXPIRED : SignPasswordChangeType.SEND;
    } else {
      isNotHavingUser.value = true;
    }
  } catch (error) {
    console.error(error);
    toastStore.openToast({
      toastType: ToastType.FAILED,
      contents: t('error-sign-wrong-approach'),
    });
  }
};

const onClickResendEmail = () => {
  if (!userEmail.value) {
    passwordChangeAuthEmailFlag.value = SignPasswordChangeType.ERROR;
    return;
  }

  if (canResendAuthEmail()) {
    void sendPasswordChangeAuthEmail(userEmail.value, true);
    setResendAuthEmailCookie();
  } else {
    toastStore.openToast({
      toastType: ToastType.FAILED,
      contents: t('password-change-resend-delay')
    });
  }
};

const userPasswordChangeAuthEmail = (userId: number, token: string) => {
  userService.userPasswordChangeAuthEmail(userId, token)
      .then((isSuccess: boolean) => {
        passwordChangeAuthEmailFlag.value = isSuccess
            ? SignPasswordChangeType.CHANGED
            : SignPasswordChangeType.EXPIRED;
      })
      .catch((e) => {
        console.error(e);
        passwordChangeAuthEmailFlag.value = SignPasswordChangeType.ERROR;
      })
      .finally(() => {
        isLoading.value = false;
      });
};

const userPasswordChange = (password: string) => {
  const userIdParam = route.query.userId as string | undefined;
  const tokenParam = route.query.token as string | undefined;

  if (!userIdParam || !tokenParam) return;

  userService.userPasswordChange(
      parseInt(userIdParam),
      tokenParam,
      sha3_256(password)).then((response: UserPasswordChangeModel) => {
    if (response.isValid) {
      passwordChangeAuthEmailFlag.value = SignPasswordChangeType.COMPLETED;
    } else {
      toastStore.openToast({
        toastType: ToastType.FAILED,
        contents: t('error-save-new-password')
      });
    }
  }).catch(() => {
    toastStore.openToast({
      toastType: ToastType.FAILED,
      contents: t('error-save-new-password')
    });
  });
};

const passwordChangeComplete = () => {
  router.push({path: '/sign'});
};

onMounted(() => {
  const userIdParam = route.query.userId as string | undefined;
  const tokenParam = route.query.token as string | undefined;

  if (userIdParam && tokenParam) {
    userPasswordChangeAuthEmail(parseInt(userIdParam, 10), tokenParam);
  } else {
    passwordChangeAuthEmailFlag.value = SignPasswordChangeType.ENTER;
    isLoading.value = false;
  }
});

watch(() => route.query.email, () => {
  if(route.query.email) {
    userEmail.value = route.query.email as string;
  }
}, { immediate: true})
</script>

<style>
@import "~/assets/scss/form_style.scss";
</style>
